import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Switch,
  TextField,
} from '@mui/material';
import { usePlans, useAssignSubscription } from '../../hooks/subscriptions';
import { useSnackbar } from '../SnackbarProvider';
import { formatPrice, formatBillingInterval } from '../../utils/subscriptions';

interface AssignPlanDialogProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  organizationName: string;
}

export const AssignPlanDialog = ({
  open,
  onClose,
  organizationId,
  organizationName,
}: AssignPlanDialogProps) => {
  const { showMessage } = useSnackbar();
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [isTrial, setIsTrial] = useState(false);
  const [trialDays, setTrialDays] = useState(14);

  const { data: plans, isLoading: plansLoading } = usePlans();
  const assignMutation = useAssignSubscription();

  const selectedPlan = plans?.find((p) => p.id === selectedPlanId);

  const handleAssign = () => {
    if (!selectedPlanId) {
      showMessage('Please select a plan', 'warning');
      return;
    }

    if (isTrial && (!trialDays || trialDays < 1)) {
      showMessage('Trial days must be at least 1', 'warning');
      return;
    }

    assignMutation.mutate(
      {
        organizationId,
        planId: selectedPlanId,
        ...(isTrial && { isTrial: true, trialDays }),
      },
      {
        onSuccess: () => {
          const successMessage = isTrial
            ? `Trial plan assigned for ${trialDays} days`
            : 'Plan assigned successfully';
          showMessage(successMessage, 'success');
          onClose();
          setSelectedPlanId('');
          setIsTrial(false);
          setTrialDays(14);
        },
        onError: (error: any) => {
          showMessage(
            error.response?.data?.message || 'Failed to assign plan',
            'error'
          );
        },
      }
    );
  };

  const handleClose = () => {
    if (!assignMutation.isPending) {
      onClose();
      setSelectedPlanId('');
      setIsTrial(false);
      setTrialDays(14);
    }
  };

  // Calculate trial expiration date for display
  const trialExpirationDate = new Date();
  trialExpirationDate.setDate(trialExpirationDate.getDate() + trialDays);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Assign Subscription Plan</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Assign a subscription plan to <strong>{organizationName}</strong>
          </Typography>

          {plansLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <>
              <FormControl fullWidth sx={{ mt: 3 }}>
                <InputLabel>Select Plan</InputLabel>
                <Select
                  value={selectedPlanId}
                  onChange={(e) => setSelectedPlanId(e.target.value)}
                  label="Select Plan"
                >
                  {plans
                    ?.filter((plan) => plan.isActive)
                    .map((plan) => (
                      <MenuItem key={plan.id} value={plan.id}>
                        {plan.name} - {formatPrice(plan.basePrice)}{' '}
                        {formatBillingInterval(plan.billingInterval)}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              {selectedPlan && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Plan Details
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {selectedPlan.description}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Price:</strong> {formatPrice(selectedPlan.basePrice)}{' '}
                    {formatBillingInterval(selectedPlan.billingInterval)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Max Users:</strong>{' '}
                    {selectedPlan.maxUsers || 'Unlimited'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Max Stores:</strong>{' '}
                    {selectedPlan.maxStores || 'Unlimited'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Max Warehouses:</strong>{' '}
                    {selectedPlan.maxWarehouses || 'Unlimited'}
                  </Typography>
                </Box>
              )}

              {/* Trial/Paid Toggle - only shown after selecting a plan */}
              {selectedPlan && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Subscription Type
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isTrial}
                        onChange={(e) => setIsTrial(e.target.checked)}
                        color="info"
                      />
                    }
                    label={isTrial ? 'Trial Period' : 'Paid Subscription'}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {isTrial
                      ? 'Start a free trial period for this organization'
                      : 'Assign as a paid subscription (billing starts immediately)'}
                  </Typography>

                  {/* Trial Days Input - only shown when trial is ON */}
                  {isTrial && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'info.lighter', borderRadius: 1 }}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Trial Duration (days)"
                        value={trialDays}
                        onChange={(e) => setTrialDays(Number(e.target.value))}
                        inputProps={{ min: 1, max: 90 }}
                        helperText={`Trial will expire on ${trialExpirationDate.toLocaleDateString()}`}
                      />
                    </Box>
                  )}
                </Box>
              )}

              {plans?.filter((p) => p.isActive).length === 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  No active plans available. Please create and activate a plan first.
                </Alert>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={assignMutation.isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleAssign}
          variant="contained"
          disabled={!selectedPlanId || assignMutation.isPending}
        >
          {assignMutation.isPending ? 'Assigning...' : 'Assign Plan'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
